/* eslint-disable prettier/prettier, react/prop-types */
import React, { useState, useEffect, useRef } from "react";
import { collection, getDocs, doc, deleteDoc, updateDoc, addDoc } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../firebase"; 

import DashboardLayout from "../../examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../examples/Navbars/DashboardNavbar";

import { Grid, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Box, LinearProgress, Chip, Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

// 🟢 Validates that a string is a valid URL slug
const isValidSlug = (value) => /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value);

export default function Printers() {
  const [printers, setPrinters] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch the logged-in user's role to control permissions
  const userRole = localStorage.getItem("userRole");

  // States for Edit Popup
  const [openEdit, setOpenEdit] = useState(false);
  const [editData, setEditData] = useState({ id: "", code: "", name: "", slug: "", brand: "", type: "", price: "", stock: true, image: "", rawSpecs: "" });
  const [editSlugError, setEditSlugError] = useState("");
  const [editImageFile, setEditImageFile] = useState(null);
  const [editImagePreview, setEditImagePreview] = useState("");
  const [editUploadProgress, setEditUploadProgress] = useState(0);
  const [editUploading, setEditUploading] = useState(false);
  const editFileInputRef = useRef(null);

  // States for Add Popup
  const [openAdd, setOpenAdd] = useState(false);
  const [newData, setNewData] = useState({ code: "", name: "", slug: "", brand: "", type: "", price: "", stock: true, image: "", rawSpecs: "" });
  const [addSlugError, setAddSlugError] = useState("");
  const [addImageFile, setAddImageFile] = useState(null);
  const [addImagePreview, setAddImagePreview] = useState("");
  const [addUploadProgress, setAddUploadProgress] = useState(0);
  const [addUploading, setAddUploading] = useState(false);
  const addFileInputRef = useRef(null);

  // 🟢 Helper function to convert a single block of raw text lines into a structured map for the website
  const convertTextToSpecsMap = (textBlock) => {
    const specsMap = {};
    if (!textBlock) return specsMap;

    // Split text by new lines
    const lines = textBlock.split(/\r?\n/);
    let currentHeader = "المميزات";

    lines.forEach((line) => {
      const cleanLine = line.trim();
      if (!cleanLine) return;

      if (cleanLine.includes(":")) {
        // Line has data separator (e.g., "السرعة: تطبع حتى...")
        const parts = cleanLine.split(":");
        const key = parts[0].trim();
        const value = parts.slice(1).join(":").trim(); // Joins back any accidental secondary colons
        specsMap[key] = value;
      } else {
        // Line is a section header line without a colon
        currentHeader = cleanLine;
        // Mark it with a "مميزات_" flag so the website renders it as a structured banner header block
        specsMap[`مميزات_${currentHeader}`] = "تابع الميزات أدناه";
      }
    });

    return specsMap;
  };

  // 🟢 Helper function to take a Firestore specs map and reconstruct it into an editable text block inside the popup textfields
  const convertSpecsMapToRawText = (specsMap) => {
    if (!specsMap || Object.keys(specsMap).length === 0) return "";
    
    return Object.entries(specsMap)
      .map(([key, value]) => {
        if (key.startsWith("مميزات_") || key.startsWith("ميزة_")) {
          return key.replace("مميزات_", "").replace("ميزة_", "");
        }
        return `${key}: ${value}`;
      })
      .join("\n");
  };

  // 🟢 Upload image to Firebase Storage and return download URL
  const uploadImage = (file, folder, onProgress) => {
    return new Promise((resolve, reject) => {
      const timestamp = Date.now();
      const storageRef = ref(storage, `products/${folder}/${timestamp}_${file.name}`);
      const uploadTask = uploadBytesResumable(storageRef, file);
      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
          onProgress(progress);
        },
        (error) => reject(error),
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          resolve(downloadURL);
        }
      );
    });
  };

  const fetchPrinters = async () => {
    setLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, "printers"));
      let printersList = querySnapshot.docs.map(document => ({
        id: document.id,
        ...document.data()
      }));

      printersList.sort((a, b) => {
        const codeA = a.code ? String(a.code) : "";
        const codeB = b.code ? String(b.code) : "";
        return codeA.localeCompare(codeB, undefined, { numeric: true });
      });

      setPrinters(printersList);
    } catch (error) {
      console.error("Error fetching printers: ", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchPrinters();
  }, []);

  // 🟢 Handle image file selection for ADD dialog
  const handleAddImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAddImageFile(file);
    setAddImagePreview(URL.createObjectURL(file));
  };

  // 🟢 Handle image file selection for EDIT dialog
  const handleEditImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setEditImageFile(file);
    setEditImagePreview(URL.createObjectURL(file));
  };

  // DELETE FUNCTION
  const handleDelete = async (id) => {
    const confirmDelete = window.confirm("هل أنت متأكد من حذف هذه الماكينة نهائياً؟"); 
    if (confirmDelete) {
      try {
        await deleteDoc(doc(db, "printers", id));
        fetchPrinters();
      } catch (error) {
        console.error("Error deleting printer: ", error);
        alert("حدث خطأ أثناء الحذف");
      }
    }
  };

  // ADD FUNCTION
  const handleAddPrinter = async () => {
    // Validate slug
    if (!newData.slug) { setAddSlugError("الـ Slug مطلوب"); return; }
    if (!isValidSlug(newData.slug)) { setAddSlugError("أحرف إنجليزية صغيرة وأرقام وشرطات فقط (مثال: ricoh-mp-4055)"); return; }
    setAddSlugError("");
    try {
      let imageURL = newData.image;
      if (addImageFile) {
        setAddUploading(true);
        imageURL = await uploadImage(addImageFile, "printers", setAddUploadProgress);
        setAddUploading(false);
      }
      const structuredSpecs = convertTextToSpecsMap(newData.rawSpecs);
      await addDoc(collection(db, "printers"), {
        code: newData.code, 
        name: newData.name,
        slug: newData.slug,
        brand: newData.brand,
        type: newData.type,
        price: Number(newData.price),
        stock: newData.stock,
        image: imageURL || "",
        specs: structuredSpecs
      });
      setOpenAdd(false);
      setNewData({ code: "", name: "", slug: "", brand: "", type: "", price: "", stock: true, image: "", rawSpecs: "" });
      setAddImageFile(null); setAddImagePreview(""); setAddUploadProgress(0);
      fetchPrinters();
    } catch (error) {
      setAddUploading(false);
      alert("حدث خطأ أثناء الإضافة");
    }
  };

  // EDIT FUNCTIONS
  const handleOpenEdit = (printer) => {
    const readableTextSpecs = convertSpecsMapToRawText(printer.specs);
    setEditData({ 
      ...printer, 
      code: printer.code || "", 
      slug: printer.slug || "",
      image: printer.image || "",
      rawSpecs: readableTextSpecs
    });
    setEditImageFile(null);
    setEditImagePreview(printer.image || "");
    setEditSlugError("");
    setOpenEdit(true);
  };

  const handleSaveEdit = async () => {
    if (!editData.slug) { setEditSlugError("الـ Slug مطلوب"); return; }
    if (!isValidSlug(editData.slug)) { setEditSlugError("أحرف إنجليزية صغيرة وأرقام وشرطات فقط (مثال: ricoh-mp-4055)"); return; }
    setEditSlugError("");
    try {
      let imageURL = editData.image;
      if (editImageFile) {
        setEditUploading(true);
        imageURL = await uploadImage(editImageFile, "printers", setEditUploadProgress);
        setEditUploading(false);
      }
      const structuredSpecs = convertTextToSpecsMap(editData.rawSpecs);
      const printerRef = doc(db, "printers", editData.id);
      await updateDoc(printerRef, {
        code: editData.code,
        name: editData.name,
        slug: editData.slug,
        brand: editData.brand,
        type: editData.type,
        price: Number(editData.price),
        stock: editData.stock,
        image: imageURL || "",
        specs: structuredSpecs
      });
      setOpenEdit(false);
      setEditImageFile(null); setEditImagePreview(""); setEditUploadProgress(0);
      fetchPrinters();
    } catch (error) {
      setEditUploading(false);
      alert("حدث خطأ أثناء التعديل");
    }
  };

  // 🟢 Reusable Image Upload Field
  const ImageUploadField = ({ preview, onFileChange, fileInputRef, uploading, progress }) => (
    <Box>
      <input type="file" accept="image/*" style={{ display: "none" }} ref={fileInputRef} onChange={onFileChange} />
      <Button
        variant="outlined" startIcon={<CloudUploadIcon />}
        onClick={() => fileInputRef.current.click()}
        fullWidth
        sx={{ textTransform: "none", borderStyle: "dashed", borderWidth: 2, py: 1.5, color: "#1A73E8", borderColor: "#1A73E8" }}
      >
        {preview ? "تغيير الصورة" : "رفع صورة من جهازك"}
      </Button>
      {uploading && (
        <Box sx={{ mt: 1 }}>
          <LinearProgress variant="determinate" value={progress} sx={{ borderRadius: 4, height: 8 }} />
          <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: "block", textAlign: "center" }}>جاري الرفع... {progress}%</Typography>
        </Box>
      )}
      {preview && !uploading && (
        <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1.5 }}>
          <img src={preview?.startsWith('/') ? `https://mr-wafaae.com${preview}` : preview} alt="preview" style={{ width: 80, height: 80, objectFit: "cover", borderRadius: 8, border: "1px solid #ddd" }} />
          <Chip label="صورة محددة ✓" color="success" size="small" />
        </Box>
      )}
    </Box>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      
      <div style={{ paddingTop: "30px", paddingBottom: "30px", direction: "rtl" }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            
            <Card sx={{ padding: "25px", borderRadius: "12px", boxShadow: "0 4px 6px rgba(0,0,0,0.1)" }}>
              
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
                <Typography variant="h5" sx={{ fontWeight: "bold", color: "#344767" }}>
                  مكينات الطباعة
                </Typography>
                
                {userRole === "admin" && (
                  <Button 
                    variant="contained" 
                    onClick={() => setOpenAdd(true)}
                    sx={{ 
                      background: "#4CAF50", 
                      color: "#fff", 
                      fontWeight: "bold",
                      "&:hover": { background: "#388E3C" } 
                    }}
                  >
                    + إضافة ماكينة جديدة
                  </Button>
                )}
              </Box>

              <TableContainer sx={{ overflowX: "auto", border: "1px solid #f0f2f5", borderRadius: "8px" }}>
                <Table sx={{ minWidth: 650 }}>
                  <TableHead sx={{ display: "table-header-group" }}>
                    <TableRow>
                      <TableCell align="center" sx={{ fontWeight: "bold", color: "#7b809a" }}>الكود</TableCell>
                      <TableCell align="center" sx={{ fontWeight: "bold", color: "#7b809a" }}>الصورة</TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold", color: "#7b809a" }}>اسم الماكينة</TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold", color: "#7b809a" }}>Slug</TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold", color: "#7b809a" }}>الماركة</TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold", color: "#7b809a" }}>النوع</TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold", color: "#7b809a" }}>السعر</TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold", color: "#7b809a" }}>المخزون</TableCell>
                      {userRole === "admin" && (
                        <TableCell align="center" sx={{ fontWeight: "bold", color: "#7b809a" }}>الإجراءات</TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center" sx={{ color: "#7b809a", padding: "20px" }}>جاري تحميل البيانات...</TableCell>
                      </TableRow>
                    ) : printers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center" sx={{ color: "#7b809a", padding: "20px" }}>لا توجد مكينات في قاعدة البيانات</TableCell>
                      </TableRow>
                    ) : (
                      printers.map((printer) => (
                        <TableRow key={printer.id} hover sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                          
                          <TableCell align="center">
                            <Typography variant="button" fontWeight="bold" color="#ed6c02">
                              {printer.code ? `[${printer.code}]` : "-"}
                            </Typography>
                          </TableCell>

                          <TableCell align="center">
                            {printer.image ? (
                              <img 
                                src={printer.image?.startsWith('/') ? `https://mr-wafaae.com${printer.image}` : printer.image} 
                                alt={printer.name} 
                                style={{ width: "50px", height: "50px", objectFit: "cover", borderRadius: "8px", border: "1px solid #ddd" }} 
                              />
                            ) : (
                              <Box sx={{ width: 50, height: 50, bgcolor: "#f0f2f5", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto" }}>
                                <Typography variant="caption" color="textSecondary">لا توجد</Typography>
                              </Box>
                            )}
                          </TableCell>

                          <TableCell align="right">
                            <Typography variant="button" fontWeight="medium" color="#344767">
                              {printer.name}
                            </Typography>
                          </TableCell>

                          <TableCell align="right">
                            <Typography variant="caption" sx={{ color: "#7b809a", fontFamily: "monospace", bgcolor: "#f5f5f5", px: 1, py: 0.5, borderRadius: 1 }}>
                              {printer.slug || "-"}
                            </Typography>
                          </TableCell>
                          
                          <TableCell align="right" sx={{ color: "#7b809a" }}>{printer.brand}</TableCell>
                          <TableCell align="right" sx={{ color: "#7b809a" }}>{printer.type}</TableCell>
                          <TableCell align="right" sx={{ color: "#7b809a" }}>{printer.price} ج.م</TableCell>
                          <TableCell align="right">
                            <Chip 
                              label={printer.stock === false ? "غير متوفر" : "متوفر"} 
                              color={printer.stock === false ? "error" : "success"} 
                              size="small" 
                              sx={{ fontWeight: "bold", minWidth: 80 }} 
                            />
                          </TableCell>
                          
                          {userRole === "admin" && (
                            <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                              <Button 
                                variant="contained"
                                size="small"
                                onClick={() => handleOpenEdit(printer)}
                                sx={{ 
                                  marginLeft: "10px",
                                  backgroundColor: "#1A73E8 !important",
                                  color: "#fff !important", 
                                }}
                              >
                                تعديل
                              </Button>

                              <Button 
                                variant="contained"
                                size="small"
                                onClick={() => handleDelete(printer.id)}
                                sx={{ 
                                  backgroundColor: "#F44336 !important",
                                  color: "#fff !important", 
                                }}
                              >
                                حذف
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Grid>
        </Grid>
      </div>

      {/* POPUP FOR ADDING NEW PRINTER */}
      <Dialog open={openAdd} onClose={() => { setOpenAdd(false); setAddSlugError(""); }} dir="rtl">
        <DialogTitle sx={{ textAlign: "right", fontWeight: "bold" }}>إضافة ماكينة جديدة</DialogTitle>
        <DialogContent style={{ display: "flex", flexDirection: "column", gap: "15px", paddingTop: "15px", minWidth: "450px" }}>
          
          <TextField 
            label="كود المنتج (اختياري)" 
            fullWidth 
            value={newData.code} 
            onChange={(e) => setNewData({...newData, code: e.target.value})} 
          />

          {/* 🟢 IMAGE UPLOAD BUTTON */}
          <ImageUploadField
            preview={addImagePreview}
            onFileChange={handleAddImageChange}
            fileInputRef={addFileInputRef}
            uploading={addUploading}
            progress={addUploadProgress}
          />

          <TextField label="اسم الماكينة (يمكن بالعربي)" fullWidth value={newData.name} onChange={(e) => setNewData({...newData, name: e.target.value})} />

          {/* 🟢 SLUG FIELD WITH VALIDATION */}
          <TextField 
            label="Slug (رابط المنتج)" 
            fullWidth 
            value={newData.slug} 
            onChange={(e) => {
              const val = e.target.value.toLowerCase();
              setNewData({...newData, slug: val});
              setAddSlugError(val && !isValidSlug(val) ? "أحرف إنجليزية صغيرة وأرقام وشرطات فقط (مثال: ricoh-mp-4055)" : "");
            }}
            error={!!addSlugError}
            helperText={addSlugError || "يُستخدم في رابط الموقع — مثال: ricoh-mp-4055"}
            inputProps={{ style: { direction: "ltr", fontFamily: "monospace" } }}
          />

          <TextField label="الماركة" fullWidth value={newData.brand} onChange={(e) => setNewData({...newData, brand: e.target.value})} />
          <TextField label="النوع" fullWidth value={newData.type} onChange={(e) => setNewData({...newData, type: e.target.value})} />
          <TextField label="السعر" type="number" fullWidth value={newData.price} onChange={(e) => setNewData({...newData, price: e.target.value})} />
          <FormControl fullWidth>
            <InputLabel>حالة المخزون</InputLabel>
            <Select value={newData.stock} label="حالة المخزون" onChange={(e) => setNewData({...newData, stock: e.target.value})}>
              <MenuItem value={true}>✅ متوفر</MenuItem>
              <MenuItem value={false}>❌ غير متوفر</MenuItem>
            </Select>
          </FormControl>
          
          {/* 🟢 UPGRADED MULTILINE SPECIFICATIONS TEXTFIELD WITH OPTIMIZED TYPOGRAPHY */}
<TextField 
  label="المواصفات الفنية والمميزات" 
  multiline 
  rows={8}
  placeholder="المواصفات الفنية الأساسية&#10;السرعة: تطبع حتى 80 ورقة في الدقيقة&#10;الدقة: تصل إلى 4800 نقطة&#10;المميزات الرئيسية&#10;الإنتاجية العالية: مصممة للعمل الشاق"
  fullWidth 
  value={newData.rawSpecs} 
  onChange={(e) => setNewData({...newData, rawSpecs: e.target.value})} 
  helperText="اكتب العنوان الرئيسي في سطر منفصل، واكتب المواصفات بصيغة (الاسم: القيمة) في سطر منفصل"
  sx={{
    "& .MuiInputBase-input": {
      fontFamily: "'Cairo', 'Segoe UI', Tahoma, sans-serif",
      fontSize: "15px",
      lineHeight: "1.8",
      color: "#222",
      letterSpacing: "0.3px",
    },
    "& .MuiInputLabel-root": {
      fontFamily: "'Cairo', sans-serif",
      fontWeight: "bold",
    },
    "& .MuiFormHelperText-root": {
      fontFamily: "'Cairo', sans-serif",
      textAlign: "right",
      color: "#666",
    }
  }}
/>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => { setOpenAdd(false); setAddSlugError(""); }}>إلغاء</Button>
          <Button onClick={handleAddPrinter} variant="contained" color="success" sx={{ color: "white" }} disabled={addUploading}>
            {addUploading ? "جاري الرفع..." : "إضافة"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* POPUP FOR EDITING PRINTER */}
      <Dialog open={openEdit} onClose={() => { setOpenEdit(false); setEditSlugError(""); }} dir="rtl">
        <DialogTitle sx={{ textAlign: "right", fontWeight: "bold" }}>تعديل بيانات الماكينة</DialogTitle>
        <DialogContent style={{ display: "flex", flexDirection: "column", gap: "15px", paddingTop: "15px", minWidth: "450px" }}>
          
          <TextField 
            label="كود المنتج (اختياري)" 
            fullWidth 
            value={editData.code} 
            onChange={(e) => setEditData({...editData, code: e.target.value})} 
          />

          {/* 🟢 IMAGE UPLOAD BUTTON */}
          <ImageUploadField
            preview={editImagePreview}
            onFileChange={handleEditImageChange}
            fileInputRef={editFileInputRef}
            uploading={editUploading}
            progress={editUploadProgress}
          />

          <TextField label="اسم الماكينة (يمكن بالعربي)" fullWidth value={editData.name} onChange={(e) => setEditData({...editData, name: e.target.value})} />

          {/* 🟢 SLUG FIELD WITH VALIDATION */}
          <TextField 
            label="Slug (رابط المنتج)" 
            fullWidth 
            value={editData.slug} 
            onChange={(e) => {
              const val = e.target.value.toLowerCase();
              setEditData({...editData, slug: val});
              setEditSlugError(val && !isValidSlug(val) ? "أحرف إنجليزية صغيرة وأرقام وشرطات فقط (مثال: ricoh-mp-4055)" : "");
            }}
            error={!!editSlugError}
            helperText={editSlugError || "يُستخدم في رابط الموقع — مثال: ricoh-mp-4055"}
            inputProps={{ style: { direction: "ltr", fontFamily: "monospace" } }}
          />

          <TextField label="الماركة" fullWidth value={editData.brand} onChange={(e) => setEditData({...editData, brand: e.target.value})} />
          <TextField label="النوع" fullWidth value={editData.type} onChange={(e) => setEditData({...editData, type: e.target.value})} />
          <TextField label="السعر" type="number" fullWidth value={editData.price} onChange={(e) => setEditData({...editData, price: e.target.value})} />
          <FormControl fullWidth>
            <InputLabel>حالة المخزون</InputLabel>
            <Select value={editData.stock} label="حالة المخزون" onChange={(e) => setEditData({...editData, stock: e.target.value})}>
              <MenuItem value={true}>✅ متوفر</MenuItem>
              <MenuItem value={false}>❌ غير متوفر</MenuItem>
            </Select>
          </FormControl>
          
          {/* 🟢 UPGRADED MULTILINE SPECIFICATIONS TEXTFIELD WITH OPTIMIZED TYPOGRAPHY */}
<TextField 
  label="المواصفات الفنية والمميزات" 
  multiline 
  rows={8}
  fullWidth 
  value={editData.rawSpecs} 
  onChange={(e) => setEditData({...editData, rawSpecs: e.target.value})} 
  helperText="يمكنك تعديل الميزات والمواصفات هنا مباشرة وسوف يتم تحديثها تلقائياً للموقع"
  sx={{
    "& .MuiInputBase-input": {
      fontFamily: "'Cairo', 'Segoe UI', Tahoma, sans-serif",
      fontSize: "15px",
      lineHeight: "1.8",
      color: "#222",
      letterSpacing: "0.3px",
    },
    "& .MuiInputLabel-root": {
      fontFamily: "'Cairo', sans-serif",
      fontWeight: "bold",
    },
    "& .MuiFormHelperText-root": {
      fontFamily: "'Cairo', sans-serif",
      textAlign: "right",
      color: "#666",
    }
  }}
/>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => { setOpenEdit(false); setEditSlugError(""); }}>إلغاء</Button>
          <Button onClick={handleSaveEdit} variant="contained" color="success" sx={{ color: "white" }} disabled={editUploading}>
            {editUploading ? "جاري الرفع..." : "حفظ التعديلات"}
          </Button>
        </DialogActions>
      </Dialog>

    </DashboardLayout>
  );
}